import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import ProfileSection from "components/Screens/Marketplace/Customer/ProfileSection/ProfileSection";
import TopHeader from "components/common/TopHeader";

import fetchTalentProfile from "services/network/talent";
import type { TalentProfile } from "components/Screens/Marketplace/Customer/types";
import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";
import PageLoader from "components/common/PageLoader";

import { getLoadingAdditionalInfo } from "selectors/user";
import { setBreadcrumbLabel } from "store/actions/Breadcrumbs";

import "components/Screens/Marketplace/Customer/ProfilePage/ProfilePage.scss";

function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch();
  const [talentData, setTalentData] = useState<TalentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const didFetchRef = useRef(false);
  const userAdditionalInfoLoading = useSelector(getLoadingAdditionalInfo);

  useEffect(() => {
    const load = async () => {
      if (!id || didFetchRef.current) return;
      didFetchRef.current = true;
      setError(null);
      try {
        const data = await fetchTalentProfile(id);
        setTalentData(data);
        const name = getTalentFullName(data);
        dispatch(setBreadcrumbLabel(location.pathname, name));
      } catch (e) {
        setTalentData(null);
        setError(e instanceof Error ? e.message : "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const title = getTalentFullName(talentData);

  if (loading || userAdditionalInfoLoading) {
    return <PageLoader />;
  }

  if (error || !talentData) {
    return (
      <div className="profile-content">
        <div className="error-message">Error loading profile: {error || "Profile not found"}</div>
      </div>
    );
  }

  return (
    <>
      <TopHeader
        title={title}
        breadcrumbs={false}
        backIcon="/main/talent-network"
        primaryBtn={undefined}
        secondaryBtn={undefined}
        subtext={undefined}
        primaryBtnClick={undefined}
        secondaryBtnClick={undefined}
      />
      <div className="profile-page">
        <div className="profile-page__container">
          <div className="profile-page__profile">
            <ProfileSection profileId={id} initialTalent={talentData} />
          </div>
        </div>
      </div>
    </>
  );
}

export default ProfilePage;
